<script lang="ts">
    import { captureError } from '@otto-ec/global-resources/apm';
    import { onMount, tick } from 'svelte';
    import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
    import type { TileListPageLoadResult } from '@tilelist/application/core/TileListSession.ts';
    import type { TileListBrowserWiring } from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
    import { createTileListBrowserWiring } from '@tilelist/application/wiring/TileListBrowserWiring.browser.ts';
    import {
        createAndProvideWiring,
        mountOrderedFullListWiring,
    } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import ScrollRestoration from '@tilelist/components/scroll-restoration/ScrollRestoration.svelte';
    import TileListPageError from '@tilelist/components/tile-list-page/TileListPageError.svelte';
    import type { ProductItem, TileItem } from '@tilelist/domain/composition/TileListitem.ts';
    import { isBlackFridaySliderItem, isProductItem } from '@tilelist/domain/composition/TileListitem.ts';
    import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage';
    import { shouldShowTopInfos } from '@tilelist/domain/feature-visibility/TopInfoVisibility';
    import { withVisualRefreshFeatureLabels } from '@tilelist/domain/tracking/VisualRefreshTracking.ts';
    import TileListPage from './TileListPage.svelte';

    const { initialSession }: { initialSession: TileListInitialSession } = $props();

    /*                                     */
    const wiringCtx = createAndProvideWiring(initialSession);

    /*                                     */
    let pageView = $state<TileListPageLayout | null>(initialSession.page);
    let cacheRestored = $state(false);
    let browserWiring: TileListBrowserWiring | null = null;

    function trackedPage(page: TileListPageLayout): TileListPageLayout {
        return wiringCtx.onex.isExperimentActive('VISUAL_REFRESH') ? withVisualRefreshFeatureLabels(page) : page;
    }

    function trackReload(wiring: TileListBrowserWiring, result: TileListPageLoadResult): void {
        if (!result.page) return;
        wiring.sendTracking(trackedPage(result.page), result.source === 'historic');
        if (result.source === 'page') wiring.trackingService.trackPagination();
    }

    const showTopInfos = $derived(pageView ? shouldShowTopInfos(pageView.context.searchContext) : false);

    onMount(() => {
        const wiring = createTileListBrowserWiring(initialSession);
        browserWiring = wiring;

        const { cleanup } = mountOrderedFullListWiring(
            wiringCtx,
            wiring,
            initialSession.page,
            async (result) => {
                if (!result.page) {
                    captureError(new Error('No page data'), { team: 'reptile', service: 'dundee' }, { type: 'reload' });
                    pageView = null;
                    return;
                }
                pageView = result.page;
                cacheRestored = true;
                await tick();

                if (result.shouldScroll) {
                    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                }

                trackReload(wiring, result);
            },
            (restored) => {
                pageView = restored;
                cacheRestored = true;
                wiring.sendTracking(trackedPage(restored));
            },
        );

        return cleanup;
    });

    /**
 *
 *
 */

    function onRedirectToPdp(item: TileItem) {
        browserWiring?.onRedirectToPdp(item, pageView);
    }

    /*                                                                                                */
    async function onSliderLoad() {
        const requestedFor = pageView?.context.rule;
        const slot = pageView?.tileListItems.find(isBlackFridaySliderItem);
        if (!slot || !wiringCtx.blackFridaySlider) return;

        const slider = await wiringCtx.blackFridaySlider.load(slot.state === 'pending' ? slot.rule : undefined);
        if (!pageView || pageView.context.rule !== requestedFor) return;

        const index = pageView.tileListItems.findIndex(isBlackFridaySliderItem);
        if (index !== -1) {
            pageView.tileListItems[index] = slider;
        }
    }

    function onProductItemUpdated(item: ProductItem) {
        if (!pageView) return;

        const index = pageView.tileListItems.findIndex(
            (i) => isProductItem(i) && i.localListPosition === item.localListPosition,
        );
        if (index !== -1) {
            pageView.tileListItems[index] = item;
        }
    }
</script>

{#if pageView}
    <ScrollRestoration
        isIOSApp={initialSession.session.app.isIOSApp}
        {cacheRestored}
        restoreScrollPosition={wiringCtx.restoreScrollPosition}
        saveScrollSelector={wiringCtx.saveScrollSelector}
    />
    <TileListPage
        {...pageView}
        isApp={initialSession.session.app.isApp}
        {showTopInfos}
        {cacheRestored}
        {onRedirectToPdp}
        {onProductItemUpdated}
        {onSliderLoad}
    />
{:else}
    <TileListPageError />
{/if}
